import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Polyline,
} from "@react-google-maps/api";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Grid,
  IconButton,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  List,
  ListItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  InputAdornment,
  CircularProgress,
  Snackbar,
  Chip,
  Tabs,
  Tab,
  Stack,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButtonGroup,
  ToggleButton,
  useMediaQuery,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Map as MapIcon,
  Search as SearchIcon,
  MyLocation as MyLocationIcon,
  Clear as ClearIcon,
  LocationOn,
  CalendarToday as CalendarIcon,
  Info as InfoIcon,
  Undo as UndoIcon,
  ExpandMore as ExpandMoreIcon,
  EditOutlined as EditOutlinedIcon,
  AltRoute as RouteIcon,
  MapOutlined as MapOutlinedIcon,
} from "@mui/icons-material";

import VehicleRouteServices from "../../services/externalServices/trackingservices/vehicleRouteServices";
import OwnerServices from "../../services/externalServices/trackingservices/ownerServices";
import googlePin from "../../../assets/images/icons/googlePin.png";
import { getOwnerIdFromLocalStorage } from "../../utils/auth";
import VehicleServices from "../../services/externalServices/trackingservices/vehicleServices";
import env from '../../../env.json';

/* ────────────────────────────────────────────────────────── */
/* Map defaults                                               */
/* ────────────────────────────────────────────────────────── */
const mapContainerStyle = {
  width: "100%",
  height: "500px",
  position: "relative",
};
const hospetCenter = { lat: 15.2697, lng: 76.3869 };
const libraries = ["places", "geometry"];

const { GOOGLE_MAP_KEY } = env;
const AddVehicleRoute = () => {
  const navigate = useNavigate();

  // Data state
  const [routeName, setRouteName] = useState("");
  const [stops, setStops] = useState([
    {
      stopName: "",
      description: "",
      latitude: "",
      longitude: "",
      plannedArrivalTime: "",
      isEnabled: true,
      address: "",
      areaName: "",
      sequence: "1",
      isPrimaryStop: true,
    },
  ]);
  const [segments, setSegments] = useState([]);
  const [newRouteSegments, setNewRouteSegments] = useState([]);
  const [assignedOwner, setAssignedOwner] = useState("");
  const [assignedVehicle, setAssignedVehicle] = useState("");
  const [vehicleList, setVehicleList] = useState([]);
  const [ownerList, setOwnerList] = useState([]);
  const [approvalNeeded, setApprovalNeeded] = useState(true);
  const [routeType, setRouteType] = useState("pickup");
  const [returnRouteId, setReturnRouteId] = useState("");
  const [approvalStatus, setApprovalStatus] = useState("Pending");
  const [isTrainingMode, setIsTrainingMode] = useState(false);
  const [aliasNumber, setAliasNumber] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  // Map state
  const [map, setMap] = useState(null);
  const [mapCenter, setMapCenter] = useState(hospetCenter);
  const [currentStopIndex, setCurrentStopIndex] = useState(null);
  const [showMapDialog, setShowMapDialog] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [currentCoordinates, setCurrentCoordinates] = useState("");

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [tab, setTab] = useState(0); // 0=Details, 1=Stops, 2=Map
  const [editMode, setEditMode] = useState("segments");
  const isSmall = useMediaQuery("(max-width:900px)");

  // Refs
  const searchInputRef = useRef(null);
  const polylineRef = useRef(null);
  const segmentsRef = useRef(segments);

  useEffect(() => {
    segmentsRef.current = segments;
  }, [segments]);

  // Google loader
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAP_KEY,
    libraries,
  });

  // Load owners
  useEffect(() => {
    (async () => {
      try {
        const res = await OwnerServices.getAllOwners();
        setOwnerList(res || []);
      } catch (error) {
        console.error("Error while getting the owners", error);
        setSnackbar({ open: true, message: "Error loading owners data.", severity: "error" });
      }
    })();
  }, []);

  // Auto select owner via localStorage email => owner._id
  useEffect(() => {
    if (ownerList.length > 0 && !assignedOwner) {
      const adminData = localStorage.getItem("admin");
      if (adminData) {
        try {
          const admin = JSON.parse(adminData);
          const adminEmail = admin.email;
          const matchingOwner = ownerList.find((o) => o.email === adminEmail);
          if (matchingOwner) setAssignedOwner(matchingOwner._id);
        } catch (e) {
          console.error("Error parsing admin from localStorage", e);
        }
      }
    }
  }, [ownerList, assignedOwner]);

  // Load vehicles for owner
  useEffect(() => {
    if (!assignedOwner) {
      setVehicleList([]);
      setAssignedVehicle("");
      return;
    }
    (async () => {
      try {
        const vehicles = await VehicleServices.getVehiclesByOwnerId(assignedOwner);
        setVehicleList(Array.isArray(vehicles) ? vehicles : []);
      } catch (e) {
        console.error("Error while getting the vehicles", e);
        setVehicleList([]);
      }
    })();
  }, [assignedOwner]);

  // Coordinates label
  useEffect(() => {
    if (mapCenter) {
      setCurrentCoordinates(`Lat: ${mapCenter.lat.toFixed(6)}, Lng: ${mapCenter.lng.toFixed(6)}`);
    }
  }, [mapCenter]);

  // Stops handlers
  const handleAddStop = () => {
    setStops((prev) => [
      ...prev,
      {
        stopName: "",
        description: "",
        latitude: "",
        longitude: "",
        plannedArrivalTime: "",
        isEnabled: true,
        address: "",
        areaName: "",
        sequence: (prev.length + 1).toString(),
        isPrimaryStop: false,
      },
    ]);
  };

  const handleStopChange = (index, field, value) => {
    const newStops = stops.map((s, i) => (i === index ? { ...s, [field]: value } : s));
    setStops(newStops);
  };

  const handleRemoveStop = (index) => {
    if (stops.length > 1) {
      const newStops = stops.filter((_, i) => i !== index);
      setStops(newStops);
    }
  };

  const handleAddStopInBetween = (index) => {
    const newStop = {
      stopName: "",
      description: "",
      latitude: "",
      longitude: "",
      plannedArrivalTime: "",
      isEnabled: true,
      address: "",
      areaName: "",
      sequence: "",
      isPrimaryStop: false,
    };
    const newStops = [...stops];
    newStops.splice(index, 0, newStop);
    setStops(newStops);
  };

  // Map handlers
  const onLoad = useCallback((m) => setMap(m), []);
  const onUnmount = useCallback(() => setMap(null), []);

  const handleMapClickForNewSegments = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    setNewRouteSegments((prev) => [...prev, { latitude: lat, longitude: lng }]);
  };

  const handleUndoNewSegment = () => {
    setNewRouteSegments((prev) => (prev.length ? prev.slice(0, -1) : prev));
  };

  const handlePromoteNewSegments = () => {
    setSegments(newRouteSegments);
    setNewRouteSegments([]);
  };

  const handleMapClick = useCallback(
    (event) => {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      if (map) map.panTo({ lat, lng });
    },
    [map]
  );

  const handleMapCenterChanged = useCallback(() => {
    if (map && currentStopIndex !== null) {
      const center = map.getCenter();
      const lat = center.lat();
      const lng = center.lng();
      setMapCenter({ lat, lng });

      const newStops = [...stops];
      newStops[currentStopIndex] = {
        ...newStops[currentStopIndex],
        latitude: lat.toString(),
        longitude: lng.toString(),
      };
      setStops(newStops);

      // Reverse geocode
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results && results) {
          const newStopsWithAddress = [...newStops];
          newStopsWithAddress[currentStopIndex] = {
            ...newStopsWithAddress[currentStopIndex],
            address: results.formatted_address,
          };
          setStops(newStopsWithAddress);
        }
      });
    }
  }, [map, currentStopIndex, stops]);

  const openLocationSelector = (index) => {
    setCurrentStopIndex(index);
    const stop = stops[index];
    if (stop.latitude && stop.longitude) {
      setMapCenter({
        lat: parseFloat(stop.latitude),
        lng: parseFloat(stop.longitude),
      });
      setSearchValue(stop.address || `${stop.latitude}, ${stop.longitude}`);
    } else {
      setMapCenter(hospetCenter);
      setSearchValue("");
    }
    setShowMapDialog(true);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (!searchValue.trim() || !map) return;
    setIsSearching(true);
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: searchValue, componentRestrictions: { country: "IN" } }, (results, status) => {
      setIsSearching(false);
      if (status === "OK" && results && results) {
        const location = results.geometry.location;
        map.panTo(location);
      } else {
        setSnackbar({ open: true, message: "Location not found. Try another search.", severity: "warning" });
      }
    });
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      setIsSearching(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setIsSearching(false);
          const newCenter = { lat: position.coords.latitude, lng: position.coords.longitude };
          if (map) map.panTo(newCenter);
        },
        () => {
          setIsSearching(false);
          setSnackbar({ open: true, message: "Unable to retrieve current location.", severity: "error" });
        }
      );
    }
  };

  const onLoadPolyline = useCallback((polyline) => {
    polylineRef.current = polyline;
    const path = polyline.getPath();

    const updateSegments = () => {
      const pathArray = path.getArray();
      const currentSegments = segmentsRef.current;
      const newSegments = pathArray.map((point, index) => ({
        ...currentSegments[index],
        latitude: point.lat(),
        longitude: point.lng(),
      }));
      setSegments(newSegments);
    };

    const listeners = [];
    listeners.push(window.google.maps.event.addListener(path, "set_at", updateSegments));
    listeners.push(window.google.maps.event.addListener(path, "insert_at", updateSegments));
    listeners.push(window.google.maps.event.addListener(path, "remove_at", updateSegments));
    polylineRef.current.pathListeners = listeners;

    return () => {
      if (polylineRef.current?.pathListeners) {
        polylineRef.current.pathListeners.forEach((l) => window.google.maps.event.removeListener(l));
      }
    };
  }, []);

  const polylinePath = useMemo(() => {
    if (segments.length === 0) return [];
    if (!window.google || !window.google.maps || !window.google.maps.geometry) {
      return segments.map((s) => ({ lat: parseFloat(s.latitude), lng: parseFloat(s.longitude) }));
    }
    if (stops.length < 2) {
      return segments.map((s) => ({ lat: parseFloat(s.latitude), lng: parseFloat(s.longitude) }));
    }
    const secondStop = stops[1];
    if (!secondStop.latitude || !secondStop.longitude) {
      return segments.map((s) => ({ lat: parseFloat(s.latitude), lng: parseFloat(s.longitude) }));
    }

    const secondStopCoords = new window.google.maps.LatLng(
      parseFloat(secondStop.latitude),
      parseFloat(secondStop.longitude)
    );

    let closestSegmentIndex = -1;
    let minDistance = Infinity;
    segments.forEach((segment, index) => {
      const segmentCoords = new window.google.maps.LatLng(
        parseFloat(segment.latitude),
        parseFloat(segment.longitude)
      );
      const distance = window.google.maps.geometry.spherical.computeDistanceBetween(secondStopCoords, segmentCoords);
      if (distance < minDistance) {
        minDistance = distance;
        closestSegmentIndex = index;
      }
    });

    const trimmed = closestSegmentIndex !== -1 ? segments.slice(closestSegmentIndex) : segments;
    return trimmed.map((s) => ({ lat: parseFloat(s.latitude), lng: parseFloat(s.longitude) }));
  }, [stops, segments]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      const processedStops = stops.map((stop) => ({
        stopName: stop.stopName,
        description: stop.description,
        isEnabled: stop.isEnabled !== undefined ? stop.isEnabled : true,
        latitude: parseFloat(stop.latitude) || 0,
        longitude: parseFloat(stop.longitude) || 0,
        plannedArrivalTime: stop.plannedArrivalTime,
        actuallArrivalTime: "",
        address: stop.address || "",
        areaName: stop.areaName || "",
        sequence: parseInt(stop.sequence) || 0,
        isPrimaryStop: !!stop.isPrimaryStop,
      }));

      const processedSegments = segments.map((segment) => ({
        latitude: parseFloat(segment.latitude),
        longitude: parseFloat(segment.longitude),
      }));

      const payload = {
        route_name: routeName,
        startTime: startTime,
        endTime: endTime,
        stops: processedStops,
        segments: processedSegments,
        assigned_owner_id: assignedOwner || null,
        approvalNeeded,
        routeType,
        returnRouteId: returnRouteId || null,
        approval_status: approvalStatus,
        isTrainingMode,
        newSegments: [],
        alias_number: aliasNumber,
        assigned_vehicle_id: assignedVehicle || null,
      };

      const created = await VehicleRouteServices.createVehicleRoute(payload);
      setSnackbar({ open: true, message: "Route created successfully!", severity: "success" });

      // Navigate to edit page if API returns the new _id
      if (created && created._id) {
        navigate(`/routes/${created._id}/edit`);
      }
    } catch (error) {
      console.error("Error creating vehicle route:", error);
      setSnackbar({ open: true, message: "Failed to create route. Please try again.", severity: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  if (loadError) return <Alert severity="error">Error loading maps</Alert>;
  if (!isLoaded) return <CircularProgress />;

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
      <Card elevation={3} sx={{ borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} flexWrap="wrap" rowGap={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h5" fontWeight="bold">Create Vehicle Route</Typography>
                <Chip icon={<InfoIcon />} label={routeType || "N/A"} color={routeType === "pickup" ? "success" : routeType === "drop" ? "info" : "default"} size="small" />
                <Chip label={approvalStatus || "Pending"} color={approvalStatus === "Approved" ? "success" : approvalStatus === "Rejected" ? "error" : "warning"} size="small" />
                {isTrainingMode && <Chip label="Training Mode" color="info" size="small" />}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                <CalendarIcon sx={{ fontSize: 14, mr: 0.5 }} />
                New Route
              </Typography>
            </Stack>
          </Box>

          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant={isSmall ? "scrollable" : "standard"} sx={{ mb: 2 }}>
            <Tab icon={<InfoIcon fontSize="small" />} iconPosition="start" label="Details" />
            <Tab icon={<EditOutlinedIcon fontSize="small" />} iconPosition="start" label={`Stops (${stops.length})`} />
            <Tab icon={<MapOutlinedIcon fontSize="small" />} iconPosition="start" label={`Map (${segments.length})`} />
          </Tabs>

          <form onSubmit={handleSubmit}>
            {tab === 0 && (
              <Paper elevation={1} sx={{ p: 3, mb: 2 }}>
                <Typography variant="h6" gutterBottom color="primary">Route Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Route Name" value={routeName} onChange={(e) => setRouteName(e.target.value)} required disabled={isSubmitting} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Start Time"
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      required
                      disabled={isSubmitting}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="End Time"
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      required
                      disabled={isSubmitting}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Assigned Owner</InputLabel>
                      <Select value={assignedOwner} label="Assigned Owner" onChange={(e) => setAssignedOwner(e.target.value)} disabled={ownerList.length === 0 || isSubmitting}>
                        {ownerList.map((owner) => (
                          <MenuItem key={owner._id} value={owner._id}>
                            {owner.name || owner.email}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Route Type</InputLabel>
                      <Select value={routeType} label="Route Type" onChange={(e) => setRouteType(e.target.value)} disabled={isSubmitting}>
                        <MenuItem value="pickup">Pickup</MenuItem>
                        <MenuItem value="drop">Drop</MenuItem>
                        <MenuItem value="both">Both</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Alias Number" value={aliasNumber} onChange={(e) => setAliasNumber(e.target.value)} required disabled={isSubmitting} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Approval Status</InputLabel>
                      <Select value={approvalStatus} label="Approval Status" onChange={(e) => setApprovalStatus(e.target.value)} disabled={isSubmitting}>
                        <MenuItem value="Pending">Pending</MenuItem>
                        <MenuItem value="Approved">Approved</MenuItem>
                        <MenuItem value="Rejected">Rejected</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Assigned Vehicle</InputLabel>
                      <Select value={vehicleList.some((v) => v._id === assignedVehicle) ? assignedVehicle : ""} label="Assigned Vehicle" onChange={(e) => setAssignedVehicle(e.target.value)} disabled={!assignedOwner}>
                        {vehicleList.map((v) => (
                          <MenuItem key={v._id} value={v._id}>{v.alias_number}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControlLabel control={<Checkbox checked={approvalNeeded} onChange={(e) => setApprovalNeeded(e.target.checked)} disabled={isSubmitting} />} label="Approval Required" />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControlLabel control={<Checkbox checked={isTrainingMode} onChange={(e) => setIsTrainingMode(e.target.checked)} disabled={isSubmitting} />} label="Training Mode" />
                  </Grid>
                </Grid>
              </Paper>
            )}

            {tab === 1 && (
              <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="h6" color="primary">Route Stops ({stops.length})</Typography>
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddStop} disabled={isSubmitting}>Add Stop</Button>
                </Stack>

                <Stack spacing={1}>
                  {stops.map((stop, index) => (
                    <Paper key={index} variant="outlined" sx={{ p: 1.5 }}>
                      <Grid container spacing={1.5} alignItems="center">
                        <Grid item xs={12} md={3}>
                          <TextField fullWidth size="small" label={`Stop ${index + 1} Name`} value={stop.stopName} onChange={(e) => handleStopChange(index, "stopName", e.target.value)} required disabled={isSubmitting} />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <TextField fullWidth size="small" label="Description" value={stop.description} onChange={(e) => handleStopChange(index, "description", e.target.value)} disabled={isSubmitting} />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <TextField fullWidth size="small" label="Area Name" value={stop.areaName} onChange={(e) => handleStopChange(index, "areaName", e.target.value)} disabled={isSubmitting} />
                        </Grid>
                        <Grid item xs={12} md={1.2}>
                          <TextField fullWidth size="small" label="Sequence" type="number" value={stop.sequence} onChange={(e) => handleStopChange(index, "sequence", e.target.value)} disabled={isSubmitting} />
                        </Grid>
                        <Grid item xs={12} md={2.3}>
                          <TextField fullWidth size="small" label="Planned Arrival" type="text" placeholder="HH:MM AM/PM" value={stop.plannedArrivalTime} onChange={(e) => handleStopChange(index, "plannedArrivalTime", e.target.value)} required disabled={isSubmitting} />
                        </Grid>
                        <Grid item xs={12} md={1.5}>
                          <Button size="small" variant="outlined" startIcon={<MapOutlinedIcon />} onClick={() => openLocationSelector(index)} disabled={isSubmitting} fullWidth>
                            {stop.latitude ? "Change" : "Select"}
                          </Button>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                            <TextField size="small" label="Latitude" value={stop.latitude} onChange={(e) => handleStopChange(index, "latitude", e.target.value)} disabled={isSubmitting} />
                            <TextField size="small" label="Longitude" value={stop.longitude} onChange={(e) => handleStopChange(index, "longitude", e.target.value)} disabled={isSubmitting} />
                          </Stack>
                          {stop.address && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                              📍 {stop.address}
                            </Typography>
                          )}
                          {stop.latitude && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                              <LocationOn sx={{ fontSize: "0.9rem", verticalAlign: "middle" }} />
                              {`Lat: ${parseFloat(stop.latitude).toFixed(5)}, Lng: ${parseFloat(stop.longitude).toFixed(5)}`}
                            </Typography>
                          )}
                        </Grid>

                        <Grid item xs={12} md="auto">
                          <FormControlLabel control={<Checkbox checked={!!stop.isEnabled} onChange={(e) => handleStopChange(index, "isEnabled", e.target.checked)} disabled={isSubmitting} />} label="Enabled" />
                        </Grid>

                        <Grid item xs={12} md="auto">
                          <FormControlLabel control={<Checkbox checked={!!stop.isPrimaryStop} onChange={(e) => handleStopChange(index, "isPrimaryStop", e.target.checked)} disabled={isSubmitting} />} label="isPrimaryStop" />
                          <IconButton color="error" onClick={() => handleRemoveStop(index)} disabled={stops.length === 1 || isSubmitting}>
                            <DeleteIcon />
                          </IconButton>
                          <Button size="small" sx={{ ml: 1 }} onClick={() => handleAddStopInBetween(index + 1)} disabled={isSubmitting}>
                            Add Here
                          </Button>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Stack>
              </Paper>
            )}

            {tab === 2 && (
              <Paper elevation={1} sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="h6" color="primary">Edit Route Segments ({segments.length})</Typography>
                  <Box />
                </Stack>

                <Box sx={{ position: "relative" }}>
                  <Box sx={mapContainerStyle}>
                    <GoogleMap
                      mapContainerStyle={{ width: "100%", height: "100%" }}
                      center={mapCenter}
                      zoom={15}
                      onLoad={onLoad}
                      onUnmount={onUnmount}
                      onClick={editMode === "segments" ? handleMapClickForNewSegments : undefined}
                      options={{ streetViewControl: false, mapTypeControl: false }}
                    >
                      <Polyline
                        key={`polyline-${polylinePath.length}`}
                        path={polylinePath}
                        options={{
                          strokeColor: "#00AA00",
                          strokeOpacity: 0.8,
                          strokeWeight: 3,
                          editable: true,
                          draggable: false,
                          geodesic: true,
                          icons: [{ icon: { path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 2, strokeColor: "#FF0000" }, repeat: "120px" }],
                        }}
                        onLoad={onLoadPolyline}
                      />

                      {stops.map((stop, index) =>
                        stop.latitude && stop.longitude ? (
                          <Marker
                            key={`stop-marker-${index}`}
                            position={{ lat: parseFloat(stop.latitude), lng: parseFloat(stop.longitude) }}
                            label={stop.stopName}
                            title={stop.stopName || `Stop ${index + 1}`}
                            options={{
                              icon: { url: "http://maps.google.com/mapfiles/ms/icons/green-dot.png", scaledSize: new window.google.maps.Size(32, 32) },
                            }}
                          />
                        ) : null
                      )}

                      <Polyline
                        path={newRouteSegments.map((s) => ({ lat: s.latitude, lng: s.longitude }))}
                        options={{ strokeColor: "#0000FF", strokeOpacity: 0.8, strokeWeight: 2, editable: true }}
                      />

                      {newRouteSegments.map((segment, index) => (
                        <Marker key={`new-segment-${index}`} position={{ lat: segment.latitude, lng: segment.longitude }} />
                      ))}
                    </GoogleMap>
                  </Box>
                </Box>
              </Paper>
            )}

            <Box sx={{ position: "sticky", bottom: -16, mt: 2, py: 1, bgcolor: "background.paper", borderTop: (t) => `1px solid ${t.palette.divider}`, zIndex: 1 }}>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button variant="outlined" onClick={handleUndoNewSegment} disabled={newRouteSegments.length === 0 || isSubmitting}>
                  Undo Last Point
                </Button>
                <Button variant="contained" onClick={handlePromoteNewSegments} disabled={newRouteSegments.length === 0 || isSubmitting}>
                  Set as Actual Segments
                </Button>
                <Button type="submit" variant="contained" startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />} disabled={isSubmitting}>
                  {isSubmitting ? "Creating…" : "Create Route"}
                </Button>
              </Stack>
            </Box>
          </form>
        </CardContent>
      </Card>

      {/* Map Dialog for selecting a stop location */}
      <Dialog open={showMapDialog} onClose={() => !isSubmitting && setShowMapDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Select Location for {stops[currentStopIndex ?? 0]?.stopName || `Stop ${(currentStopIndex ?? 0) + 1}`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <form onSubmit={handleSearchSubmit}>
              <TextField
                fullWidth
                inputRef={searchInputRef}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="Search for a location"
                variant="outlined"
                size="small"
                disabled={isSubmitting}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {isSearching ? <CircularProgress size={20} /> : <SearchIcon />}
                    </InputAdornment>
                  ),
                  endAdornment: searchValue && (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setSearchValue("")} size="small" disabled={isSubmitting}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </form>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
              <Button startIcon={<MyLocationIcon />} onClick={getCurrentLocation} size="small" disabled={isSubmitting}>
                Use Current Location
              </Button>
              <Typography variant="caption" color="text.secondary">{currentCoordinates}</Typography>
            </Box>
          </Box>

          <Box sx={mapContainerStyle}>
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "100%" }}
              center={mapCenter}
              zoom={15}
              onLoad={onLoad}
              onUnmount={onUnmount}
              onClick={!isSubmitting ? handleMapClick : undefined}
              onIdle={!isSubmitting ? handleMapCenterChanged : undefined}
              options={{ streetViewControl: false, mapTypeControl: false }}
            >
              <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -100%)", pointerEvents: "none" }}>
                <img src={googlePin} alt="Map Pin" style={{ width: "40px", height: "40px" }} />
              </Box>

              {stops.map((stop, index) =>
                stop.latitude && stop.longitude ? (
                  <Marker
                    key={`stop-marker-dialog-${index}`}
                    position={{ lat: parseFloat(stop.latitude), lng: parseFloat(stop.longitude) }}
                    label={stop.stopName}
                    title={stop.stopName || `Stop ${index + 1}`}
                  />
                ) : null
              )}
            </GoogleMap>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMapDialog(false)} variant="outlined" disabled={isSubmitting}>Cancel</Button>
          <Button onClick={() => setShowMapDialog(false)} variant="contained" disabled={isSubmitting}>Done</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AddVehicleRoute;